
import {
    DocumentPreviewStatus,
    DocumentView,
    ShareBaseDocument,
    MediaPreviewStatus
} from 'phoenix/domain';

export interface MediaPreviewStatusUpdateCallback {
    (doc: ShareBaseDocument, previewStatus: MediaPreviewStatus);
}

/**
 * Abstracts state of pinging the server for video preview status.
 */
export class MediaPreview {

    private latestStatus: MediaPreviewStatus = null;
    private _continue: boolean = true;
    private _running: boolean = false;
    private updateSubs: MediaPreviewStatusUpdateCallback[] = [];

    /**
     * Check if the object is actively pinging the server.
     */
    public get isRunning(): boolean {
        return this._running;
    }

    constructor(
        private readonly $timeout: ng.ITimeoutService,
        private readonly $http: ng.IHttpService,
        private readonly doc: DocumentView,
        private readonly pid: string = ''
    ) { }

    /**
     * If the object isn't currently pinging the server, start pinging it.
     */
    public start(timeDelay: number = 0) {
        if (!this.isRunning) {
            this._continue = true;
            this.getPrivewStatus(timeDelay);
        }
    }

    /**
     * Tell the object to stop pinging the server, even if the preview generation
     * is not ready.
     */
    public stop(): void {
        this._continue = false;
        this._running = false;
    }

    /**
     * Subscribe to status updates.
     * 
     * @param sub The subscriber function
     * @returns A function that can be called to unsubscribe
     */
    public subscribe(sub: MediaPreviewStatusUpdateCallback): () => void {
        this.updateSubs.push(sub);
        if (this.latestStatus) {
            sub(this.doc.Embedded.Document, this.latestStatus);
        }

        return () => {
            let i = this.updateSubs.indexOf(sub);
            if (i >= 0) {
                this.updateSubs = [
                    ...this.updateSubs.slice(0, i),
                    ...this.updateSubs.slice(i + 1)
                ];
            }
        };
    }

    private getPrivewStatus(timeDelay: number = 0) {
        this._running = true;
        this.$timeout(() => {
            this.getPreviewStatusUpdate()
                .then((status) => {
                    this.latestStatus = status;
                    this.updateSubs.forEach((sub) => sub(this.doc.Embedded.Document, status));
                    if (status.PreviewStatus === 'InProgress' && this._continue) {
                        // If the preview generation is still in progress, and the consumer
                        // hasn't explicitly stopped the pinging, call this method again to
                        // get the next status update.
                        this._running = true;
                        this.getPrivewStatus(1000);
                    } else {
                        this._running = false;
                    }
                });
        }, timeDelay);
    }

    private getPreviewStatusUpdate() {
        return this.$http.get(this.doc.PageTemplate)
            .then(res => res.data as MediaPreviewStatus);
    }
}

// Cache requests to get preview generation statuses.
// This way we will only have one object that is actively
// pinging the server.
let mediaPreviewCache: { [docId: number]: MediaPreview } = { };

export class MediaService {
    public static readonly $inject = [
        '$timeout',
        '$http',
        'pxUriPathService'
    ];

    constructor(
        private readonly $timeout: ng.ITimeoutService,
        private readonly $http: ng.IHttpService,
        private readonly path
    ){        
    }

    public getPreviewStatus(docView: DocumentView): MediaPreview {
        let preview = mediaPreviewCache[docView.Embedded.Document.DocumentId];
        if (!preview) {
            preview = new MediaPreview(this.$timeout, this.$http, docView);
            mediaPreviewCache[docView.Embedded.Document.DocumentId] = preview;

            let previewSub: () => void;
            // when it has completed, remove it from the cache
            previewSub = preview.subscribe((doc, status) => {
                if (status.PreviewStatus === 'Available') {
                    mediaPreviewCache[doc.DocumentId] = null;
                    delete mediaPreviewCache[doc.DocumentId];
                    if (previewSub) {
                        previewSub();
                    }
                }
            });
        }
        
        if (!preview.isRunning) {
            preview.start();
        }

        return preview;
    }

    public getPreview(id: number): MediaPreview {
        return mediaPreviewCache[id] || null;
    }

    public isVideo(doc: ShareBaseDocument): boolean {
        return doc.FileFormat.toLowerCase() === 'video';
    }

    public isAudio(doc: ShareBaseDocument): boolean {
        return doc.FileFormat.toLowerCase().indexOf('audio') >= 0;
    }

    public isMediaDocument(doc: ShareBaseDocument): boolean {
        return this.isVideo(doc) || this.isAudio(doc);
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/mediaService.ts